import PropTypes from 'prop-types';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Box, Button, Link, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useTransl } from 'shared/lib/i18n';

import ManageUserContainer from '../Person/ManageUserContainer';
import VisitorProfileLogContainer from './VisitorProfileLogContainer';
import VisitorProfilePTContainer from './VisitorProfilePTContainer';

import BackButtonComponent from '../Forms/BackButtonComponent';
import ExpandableImageComponent from '../Image/ExpandableImageComponent';

import {
  avatarPlaceholderSRC,
  formatDate,
  isAdmin,
  isPM,
  replaceUnderscoreWithDash,
  toSentenceCase,
} from '../../utils';
import { getLinkToUser } from '../Messages/utils';
import {
  checkHasRole,
  findPersonEmail,
  findPersonPhoneNumber,
  getFullName,
  getRoleName,
} from '../Person/utils';
import {
  canEditBroker,
  canEditVisitor,
  getAuthInfo,
  getPropertyInfo,
} from './utils';

import { roleNames } from '../Person/constants';

import Building from '../../res/svg/Building';
import Calendar from '../../res/svg/Calendar';
import Downloaded from '../../res/svg/Downloaded';
import Key from '../../res/svg/Key';
import Mail from '../../res/svg/Mail';
import Phone from '../../res/svg/Phone';
import Registered from '../../res/svg/Registered';
import { history } from '../../store';
import AdminAction from '../AdminAction';
import { ActionButton } from '../Button';
import WithLink from '../Linkify';
import StyledLink from '../StyledLink';
import CellPersonWithImage from '../Table/CellPersonWithImage';
import ListActions from '../Table/ListActions';

const StyledSubtitle = styled(Box)(({ theme }) => ({
  listStyle: 'none',
  '&::before': {
    content: "'-'",
    color: theme.palette.common.black,
    fontWeight: 'bold',
    display: 'inline-block',
    width: '1.6rem',
  },
}));

const StyledVisitorValidInfo = styled(Typography)(() => ({
  textTransform: 'lowercase',
  '&::first-letter': {
    textTransform: 'uppercase',
  },
}));

const getHostedByInfo = (authorizer, creator) => {
  const { t } = useTransl();
  const authorizerUrl = getLinkToUser(authorizer, true);
  const authorizerInfo = (
    <Box display="flex" alignItems="center">
      <Typography>{t('visitor.hostedBy')}&nbsp;</Typography>
      <CellPersonWithImage
        person={authorizer}
        linkUrl={authorizerUrl}
        isShowImg={false}
      />
    </Box>
  );
  if (authorizer?.id === creator?.id) {
    return authorizerInfo;
  }
  const creatorUrl = getLinkToUser(creator, true);
  const creatorInfo = (
    <Box display="flex" alignItems="center">
      <Typography sx={{ textTransform: 'lowercase' }}>
        ({t('title.createdBy')}&nbsp;
      </Typography>
      <CellPersonWithImage
        person={creator}
        linkUrl={creatorUrl}
        isShowImg={false}
      />
      <Typography>)</Typography>
    </Box>
  );

  return (
    <Box display="flex" alignItems="center">
      {authorizerInfo}&nbsp;{creatorInfo}
    </Box>
  );
};

const getVisitorInfo = (visitor, doors, roleName) => {
  const { t } = useTransl();

  const {
    person,
    invite,
    appInstalled,
    authInfo,
    allowedTimeEnd,
    allowedTimeStart,
  } = visitor;
  const data = [];

  const propertyInfo = getPropertyInfo(visitor);
  const { id, name, type } = propertyInfo;

  if (id && name && type) {
    const buildingInfo = {
      icon: <Building />,
      title: <StyledLink to={`/${type}/${id}`}>{name}</StyledLink>,
    };
    if (doors?.length) {
      buildingInfo.subtitle = doors.map((door) => (
        <StyledLink
          key={door.id}
          to={`/building/${door.building}/doors/${door.id}`}
        >
          {t('doors.doorAccess', { name: door.name })}
        </StyledLink>
      ));
    }
    data.push(buildingInfo);
  }

  const contactEmail = findPersonEmail(person);
  const contactPhone = findPersonPhoneNumber(person);
  if (contactEmail) {
    data.push({
      icon: <Mail />,
      title: (
        <Link
          href={`mailto:${contactEmail}`}
          sx={(theme) => ({
            color: theme.palette.custom.link,
          })}
        >
          {contactEmail}
        </Link>
      ),
    });
  }
  if (contactPhone) {
    data.push({
      icon: <Phone />,
      title: contactPhone,
    });
  }

  let validFrom = visitor?.validFrom;
  if (validFrom) {
    validFrom = `${t('title.validFrom')} ${formatDate(validFrom, false)}`;
  }

  let validUntil = visitor?.validUntil;
  if (validUntil) {
    if (Date.now() > Date.parse(validUntil)) {
      validUntil = `${t('visitor.expired')} ${roleName}`;
    } else {
      validUntil = `${t('title.validUntil')} ${formatDate(validUntil, false)}`;
    }
  } else {
    validUntil = `${t('visitor.permanent')} ${roleName}`;
  }
  if (validUntil) {
    data.push({
      icon: <Calendar />,
      title: (
        <StyledVisitorValidInfo>
          {[validFrom, validUntil].filter(Boolean).join(', ')}
        </StyledVisitorValidInfo>
      ),
    });
  }
  if (invite && invite.type) {
    data.push({
      icon: <Key />,
      title: `${t('visitor.keyAuth')} (${
        invite.type === 'short_term'
          ? t('visitor.fifteenMinKey')
          : t('visitor.appInvite')
      })`,
    });
    const authType = getAuthInfo(authInfo);
    if (invite.type !== 'short_term' && authType) {
      data.push({
        icon: <Registered />,
        title: authType,
      });
    }
  }

  if (allowedTimeStart && allowedTimeEnd) {
    const from = allowedTimeStart.substring(0, 5);
    const to = allowedTimeEnd.substring(0, 5);
    data.push({
      icon: <AccessTimeIcon />,
      title: t('visitor.allowedHoursFromTo', { from, to }),
    });
  }

  if (appInstalled) {
    data.push({
      icon: <Downloaded />,
      title: t('visitor.appDownloaded'),
    });
  }

  return data.map((item, i) => (
    <Box display="flex" mb={1} key={item.title + i}>
      <Box width="2.4rem" textAlign="center">
        {item.icon}
      </Box>
      <Box display="flex" flexDirection="column" ml={2}>
        <Typography>{item.title}</Typography>
        {item?.subtitle?.map((subtitle, i) => (
          <StyledSubtitle key={subtitle + i}>{subtitle}</StyledSubtitle>
        ))}
      </Box>
    </Box>
  ));
};

const VisitorProfileComponent = (props) => {
  const { t } = useTransl();
  const {
    visitor,
    doors,
    openManageUserModal,
    getVisitorInfoById,
    onLogEntry,
    match,
  } = props;
  const { person, authorizer, role: personRole, creator, inviteSent } = visitor;
  const visitorName = getFullName(person);
  const roleName = getRoleName(personRole);
  const roleTitle = toSentenceCase(roleName);
  const isBroker = checkHasRole(roleNames.broker, visitor?.role);
  const canView = isAdmin() || isPM(match?.params) || canEditVisitor(visitor);
  const canEdit = isBroker
    ? canEditBroker(match?.params)
    : canEditVisitor(visitor);

  const editButton = {
    title: canEdit
      ? `${t('actions.edit')} ${roleTitle}`
      : t('events.viewDetails'),
    onClick: () =>
      history.push(
        `/visitors/manage/update-${replaceUnderscoreWithDash(roleName)}/${
          visitor.id
        }`,
      ),
  };

  const modalProps = {
    title: t('title.manageUser'),
    render: (props) => (
      <ManageUserContainer
        {...props}
        person={person}
        personId={person?.id}
        onCredentialsUpdate={getVisitorInfoById}
      />
    ),
  };

  const customActions = [
    {
      label: <AdminAction>{t('title.manageUser')}</AdminAction>,
      onClick: () => openManageUserModal(modalProps),
      show: inviteSent && isAdmin(),
    },
  ];

  return (
    <section>
      <ExpandableImageComponent
        src={avatarPlaceholderSRC}
        photo={person?.photo}
        caption={visitorName}
        height="15rem"
        isSquare
      />
      <Box mt={2}>
        <Typography variant="h5">
          {visitorName} - {roleTitle}
        </Typography>
      </Box>
      {getHostedByInfo(authorizer, creator)}
      <Box mt={2}>
        <Typography variant="h6" fontStyle="italic">
          <WithLink>{visitor.comments}</WithLink>
        </Typography>
      </Box>
      <Box mt={2}>{getVisitorInfo(visitor, doors, roleTitle)}</Box>

      <Box display="flex" mt={3}>
        {!isAdmin() && (
          <>
            <ActionButton onClick={() => onLogEntry()}>
              {t('title.logEntry')}
            </ActionButton>
            <Box ml={1} />
          </>
        )}
        {canView && (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            onClick={editButton.onClick}
          >
            {editButton.title}
          </Button>
        )}
        <Box ml="1rem">
          <BackButtonComponent />
        </Box>
        <Box ml="1rem">
          <ListActions actions={customActions} />
        </Box>
      </Box>
      <Box my={5}>
        <Typography variant="h5">{t('title.entryLog')}</Typography>
        <VisitorProfileLogContainer visitorId={visitor.id} />
      </Box>
      {roleName === 'broker' && (
        <Box my={5}>
          <Typography variant="h5">
            {t('visitor.prospectiveTenants')}
          </Typography>
          <VisitorProfilePTContainer visitorId={visitor.id} match={match} />
        </Box>
      )}
    </section>
  );
};

VisitorProfileComponent.propTypes = {
  openManageUserModal: PropTypes.func.isRequired,
  getVisitorInfoById: PropTypes.func.isRequired,
  onLogEntry: PropTypes.func.isRequired,
  visitor: PropTypes.instanceOf(Object).isRequired,
  doors: PropTypes.instanceOf(Array).isRequired,
  match: PropTypes.instanceOf(Object).isRequired,
};

export default VisitorProfileComponent;
